import {
  EULER_USD_UNIT_OF_ACCOUNT,
  E_LEND_CHAIN_ID,
  E_LEND_GOVERNED_PERSPECTIVE,
  SHOW_ALL_ELEND_VAULTS,
  USD_REFERENCE_TOKEN,
} from '@/config/euler-contracts'
import {
  type NormalizedELendInfo,
  type NormalizedTokenInfo,
  type SubAccountBalance,
  getTokenFromMap,
  useDefaultELendsList,
  useTokensInfo,
  useVaultUserBalances,
} from '@/domain/euler'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { getEntities } from '@/features/evault-details/data/entities'
import { type VaultMarket, getVaultMarket } from '@/features/evault-details/data/products'
import { selectGovernorsFromAddresses } from '@/features/evault-details/logic/selectGovernors'
import { useMemo } from 'react'
import { type Address, formatUnits } from 'viem'
import { useBatchELendAPY } from './useBatchELendAPY'

export { E_LEND_CHAIN_ID, USD_REFERENCE_TOKEN }
export const E_LEND_GOVERNED_PERSPECTIVE_ADDRESS_CHECKED = E_LEND_GOVERNED_PERSPECTIVE
export const USD_UNIT_OF_ACCOUNT = EULER_USD_UNIT_OF_ACCOUNT

export interface ELendVaultData {
  address: Address
  name: string
  symbol: string
  assetSymbol?: string
  underlyingTokenInfo?: NormalizedTokenInfo // Full token info from Euler indexer (includes logoURI, decimals, etc.)
  decimals: number
  assetDecimals: number
  totalSupply: NormalizedUnitNumber
  totalBorrowed: NormalizedUnitNumber
  totalAssets: NormalizedUnitNumber
  tvlUsd?: NormalizedUnitNumber
  assetPrice?: number | null // Oracle price for the underlying asset in USD
  underlyingAsset?: Address
  userBalance?: NormalizedUnitNumber
  walletBalance?: NormalizedUnitNumber
  walletBreakdown?: { nativeUsd: number; wrappedUsd: number; nativeAmount: number; wrappedAmount: number }
  userDeposit?: NormalizedUnitNumber // User's deposited amount in underlying asset
  userDepositUsd?: NormalizedUnitNumber // User's deposited amount in USD
  token?: Token
  supplyAPY?: number
  /** 30-day rolling average supply APY from indexer */
  supplyApy30d?: number | null
  /** Lending APY from borrower interest (on-chain calculation) */
  lendingAPY?: number
  /** Intrinsic APY from underlying asset (e.g., staking yield, PT yield) - from indexer */
  intrinsicAPY?: number
  borrowAPY?: number
  curatorName?: string
  curatorLogo?: string
  market?: VaultMarket
  points?: string[]
  /** The first subaccount that has a non-zero balance for this vault */
  firstSubAccountWithBalance?: number
  /** All sub-accounts with non-zero balances for this vault */
  subAccountBalances?: SubAccountBalance[]
  /** Whether this vault is only visible in dev mode (not in governed perspective) */
  isDevOnly?: boolean
  /** Exposure data - collaterals this vault accepts */
  exposure?: {
    collateral: Address
    vaultAsset: Address | null
    vaultName: string | null
    borrowLTV: string
    liquidationLTV: string
    targetTimestamp: string
  }[]
  /** Utilization rate (0-1) */
  utilization?: number
}

export interface UseELendResult {
  vaults: ELendVaultData[]
  isLoading: boolean
  error: Error | null
}

export interface UseELendOptions {
  /**
   * When true, aggregates the connected user's balances across the fallback
   * subaccount range (0..15) in addition to any actively discovered
   * subaccounts. Defaults to `true` so existing call sites (standalone ELend
   * view, deposit/withdraw dialogs, etc.) keep their behavior.
   *
   * Listing/overview surfaces that don't render per-subaccount position data
   * should pass `false` to avoid the N-vaults × ~16-subaccounts multicall.
   */
  includeSubAccountPositions?: boolean
}

export function useELend(userAddress?: Address, options: UseELendOptions = {}): UseELendResult {
  const { includeSubAccountPositions = true } = options
  // Step 1: Fetch vaults from Euler Indexer
  // Uses SHOW_ALL_ELEND_VAULTS config to determine if perspective filtering is applied
  // This hook includes onchain fallback if indexer fails
  const {
    vaults: indexerVaults,
    isPending: isPendingIndexer,
    error: fetchError,
  } = useDefaultELendsList(E_LEND_CHAIN_ID)

  // Get vault addresses for user data fetching
  const vaultAddresses = useMemo(() => indexerVaults.map((v) => v.address), [indexerVaults])

  const { apyMap: liveApyMap } = useBatchELendAPY({
    vaultAddresses,
    enabled: vaultAddresses.length > 0,
  })

  // Step 2: Fetch underlying asset addresses from indexer for token info
  const underlyingAssetAddresses = useMemo(
    () => indexerVaults.filter((v) => v.asset).map((v) => v.asset as Address),
    [indexerVaults],
  )

  // Use Euler indexer for token info (symbol, decimals, logoURI) with onchain fallback
  const { tokens: indexerTokens } = useTokensInfo(E_LEND_CHAIN_ID, underlyingAssetAddresses)

  // Step 3: Fetch user-specific data (balances) using shared hook
  const underlyingAssets = useMemo(() => indexerVaults.map((v) => v.asset ?? undefined), [indexerVaults])
  const decimalsArray = useMemo(() => indexerVaults.map((v) => v.assetDecimals ?? v.decimals), [indexerVaults])

  const { balances: userBalances } = useVaultUserBalances({
    vaultAddresses,
    underlyingAssets,
    decimals: decimalsArray,
    userAddress,
    chainId: E_LEND_CHAIN_ID,
    enabled: !!userAddress,
    includeSubAccounts: includeSubAccountPositions,
  })

  // Step 4: Process and combine all data
  const vaults: ELendVaultData[] = indexerVaults.map((indexerVault) => {
    const decimals = indexerVault.decimals
    const assetDecimals = indexerVault.assetDecimals ?? decimals

    // Get underlying asset info from Euler indexer (with onchain fallback)
    const underlyingTokenInfo = getTokenFromMap(indexerTokens, indexerVault.asset ?? undefined)
    const assetSymbol = underlyingTokenInfo?.symbol ?? indexerVault.assetSymbol ?? undefined

    // Get user balances from shared hook
    const userBalanceData = userBalances.get(indexerVault.address)
    const userBalanceRaw = userBalanceData?.vaultBalance
    const walletBalanceRaw = userBalanceData?.walletBalance
    const rawBreakdown = userBalanceData?.walletBalanceBreakdown
    const depositAssetsFormatted = userBalanceData?.depositAssetsFormatted
    const firstSubAccountWithBalance = userBalanceData?.firstSubAccountWithBalance
    const subAccountBalances = userBalanceData?.subAccountBalances

    // Convert TVL to NormalizedUnitNumber
    // Priority: indexer totalAssetsUSD → calculated from totalAssets * assetPrice
    const totalAssetsNormalized = Number(formatUnits(indexerVault.totalAssets, assetDecimals))
    const indexerAssetPrice = indexerVault.assetPrice ?? undefined
    let tvlUsd: NormalizedUnitNumber | undefined

    if (
      indexerVault.totalAssetsUSD !== undefined &&
      indexerVault.totalAssetsUSD !== null &&
      indexerVault.totalAssetsUSD > 0
    ) {
      tvlUsd = NormalizedUnitNumber(indexerVault.totalAssetsUSD.toString())
    } else if (totalAssetsNormalized === 0) {
      tvlUsd = NormalizedUnitNumber('0')
    } else if (indexerAssetPrice !== undefined && indexerAssetPrice > 0) {
      tvlUsd = NormalizedUnitNumber((totalAssetsNormalized * indexerAssetPrice).toString())
    }

    // useBatchELendAPY canonicalizes its keys on lowercase so the shared cache
    // entry is safe to read regardless of which caller populated it first.
    const liveApy = liveApyMap.get(indexerVault.address.toLowerCase())
    const borrowAPY = liveApy?.borrowAPY ?? indexerVault.baseApy ?? 0
    const utilization = liveApy?.utilization ?? indexerVault.utilization ?? 0

    const intrinsicAPY = indexerVault.intrinsicApy?.apy ?? undefined

    const liveSupplyAPY = liveApy?.supplyAPY
    const supplyAPY =
      liveSupplyAPY !== undefined
        ? liveSupplyAPY + (intrinsicAPY ?? 0)
        : indexerVault.totalApy ?? borrowAPY * utilization

    const lendingAPY = intrinsicAPY !== undefined && intrinsicAPY > 0 ? supplyAPY - intrinsicAPY : supplyAPY

    const curatorName = getCuratorName(indexerVault)
    const curatorLogo = getCuratorLogo(indexerVault)
    const market = getVaultMarket(indexerVault.address, indexerVault.products)

    // In dev mode, flag vaults that aren't in the governed perspective
    const isDevOnly =
      SHOW_ALL_ELEND_VAULTS &&
      (!indexerVault.perspectives ||
        !indexerVault.perspectives.some((p) => p.toLowerCase() === E_LEND_GOVERNED_PERSPECTIVE.toLowerCase()))

    // Calculate user deposit in USD
    let userDepositUsd: number | undefined
    if (depositAssetsFormatted !== undefined && indexerAssetPrice !== undefined && indexerAssetPrice > 0) {
      userDepositUsd = Number(depositAssetsFormatted) * indexerAssetPrice
    }

    return {
      address: indexerVault.address,
      name: indexerVault.name,
      symbol: indexerVault.symbol,
      assetSymbol,
      underlyingTokenInfo,
      decimals,
      assetDecimals,
      totalSupply: NormalizedUnitNumber(formatUnits(indexerVault.totalShares, decimals)),
      totalBorrowed: NormalizedUnitNumber(formatUnits(indexerVault.totalBorrows, assetDecimals)),
      totalAssets: NormalizedUnitNumber(formatUnits(indexerVault.totalAssets, assetDecimals)),
      tvlUsd,
      assetPrice: indexerAssetPrice,
      underlyingAsset: indexerVault.asset ?? undefined,
      userBalance:
        userBalanceRaw !== undefined ? NormalizedUnitNumber(formatUnits(userBalanceRaw, decimals)) : undefined,
      walletBalance:
        walletBalanceRaw !== undefined && assetDecimals !== undefined
          ? NormalizedUnitNumber(formatUnits(walletBalanceRaw, assetDecimals))
          : undefined,
      walletBreakdown: (() => {
        if (!rawBreakdown || !indexerAssetPrice || indexerAssetPrice <= 0) return undefined
        const nativeAmount = Number(rawBreakdown.nativeBalanceFormatted)
        const wrappedAmount = Number(rawBreakdown.wrappedBalanceFormatted)
        if (nativeAmount <= 0 && wrappedAmount <= 0) return undefined
        return {
          nativeAmount,
          wrappedAmount,
          nativeUsd: nativeAmount * indexerAssetPrice,
          wrappedUsd: wrappedAmount * indexerAssetPrice,
        }
      })(),
      userDeposit: depositAssetsFormatted !== undefined ? NormalizedUnitNumber(depositAssetsFormatted) : undefined,
      userDepositUsd: userDepositUsd !== undefined ? NormalizedUnitNumber(userDepositUsd) : undefined,
      supplyAPY,
      supplyApy30d: indexerVault.supplyApy30d,
      lendingAPY,
      intrinsicAPY,
      borrowAPY,
      curatorName,
      curatorLogo,
      market,
      isDevOnly,
      points: indexerVault.points,
      firstSubAccountWithBalance,
      subAccountBalances,
      // For indexer data: null exposure means collateral-only vault → map to empty array
      // For onchain fallback: exposure data isn't available → keep as undefined
      exposure:
        indexerVault.source === 'onchain'
          ? undefined
          : indexerVault.exposure?.map((e) => ({
              collateral: e.collateral,
              vaultAsset: e.vaultAsset,
              vaultName: e.vaultName,
              borrowLTV: e.borrowLTV,
              liquidationLTV: e.liquidationLTV,
              targetTimestamp: e.targetTimestamp,
            })) ?? [],
      utilization,
    }
  })

  // Show loading only for critical vault data
  // Token info and user balances will show skeletons in cards while loading
  const isLoading = isPendingIndexer
  // Only show error if both indexer AND onchain fallback failed (error from useDefaultELendsList)
  const error = fetchError

  return {
    vaults,
    isLoading,
    error,
  }
}

export function getCuratorLogo(vault: NormalizedELendInfo): string | undefined {
  if (vault.governorAdmin) {
    const entities = getEntities()
    const governorData = selectGovernorsFromAddresses(vault.governorAdmin, entities)
    if (governorData.governors[0]?.logo) {
      return governorData.governors[0].logo
    }
  }

  if (vault.products && vault.products.length > 0) {
    const product = vault.products[0]
    if (product) {
      const entityId = product.entity?.[0]
      if (entityId && vault.entities) {
        const entity = vault.entities.find((e) => e.entity === entityId)
        if (entity?.logo) {
          return entity.logo
        }
      }
    }
  }

  if (vault.entities && vault.entities.length > 0) {
    const entity = vault.entities[0]
    if (entity?.logo) {
      return entity.logo
    }
  }

  const market = getVaultMarket(vault.address, vault.products)
  if (market) {
    const entities = getEntities()
    const entityId = Array.isArray(market.entity) ? market.entity[0] : market.entity
    if (entityId) {
      const entity = entities.get(entityId)
      if (entity?.logo) {
        return entity.logo
      }
    }
  }

  return undefined
}

export function getCuratorName(vault: NormalizedELendInfo): string {
  // 1. Try to resolve curator name from governor address (same approach as details page)
  // This is the most reliable method as it matches governor address to entities
  if (vault.governorAdmin) {
    const entities = getEntities()
    const governorData = selectGovernorsFromAddresses(vault.governorAdmin, entities)
    if (governorData.governors[0]?.name) {
      return governorData.governors[0].name
    }
  }

  // 2. Try indexer data - Check products for entity names
  if (vault.products && vault.products.length > 0) {
    const product = vault.products[0]
    if (product) {
      // Look up entity name from entities array
      const entityId = product.entity?.[0]
      if (entityId && vault.entities) {
        const entity = vault.entities.find((e) => e.entity === entityId)
        if (entity) {
          return entity.name
        }
      }
    }
  }

  // 3. Fall back to indexer entities array
  if (vault.entities && vault.entities.length > 0) {
    const entity = vault.entities[0]
    if (entity) {
      return entity.name
    }
  }

  // 4. Fall back to frontend market/entity data
  // Normalize vault address to lowercase for consistent comparison
  const market = getVaultMarket(vault.address, vault.products)
  if (market) {
    // Get entity name from frontend entities map
    const entities = getEntities()
    const entityId = Array.isArray(market.entity) ? market.entity[0] : market.entity
    if (entityId) {
      const entity = entities.get(entityId)
      if (entity) {
        return entity.name
      }
    }
  }

  // 5. Fall back to governor type
  if (vault.governorType === 'KNOWN') {
    return 'Known Governor'
  }
  if (vault.governorType === 'GOVERNED') {
    return 'Governed'
  }

  // 6. Last resort: empty string (will fall back to vault name in UI)
  return ''
}
